import { BrowserRouter, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { Box, Button, Spinner } from '@hyphen/hyphen-components';

import mark from '../../assets/static/logo.svg';
import { useSession } from '../lib/session.js';
import { SignIn } from './sign-in.jsx';
import { ControlsList } from './controls-list.jsx';
import { ControlDetail } from './control-detail.jsx';
import { Pending } from './pending.jsx';
import { GateAdmin } from './gate.jsx';
import { EvidenceAdmin } from './evidence.jsx';
import { EvidenceRun } from './evidence-run.jsx';
import { UsersAdmin } from './users.jsx';
import { AccessReviews, AccessReview } from './access-reviews.jsx';
import { Portal } from './portal.jsx';
import { PortalAdmin } from './portal-admin.jsx';

/**
 * The app: the public trust portal at the root, and the admin under /admin.
 *
 * Split before anything loads, because the two have nothing in common but the
 * bundle. The portal is for customers, signed in with their Hyphen account or
 * not at all; the admin is for Hyphen and the people working on the controls
 * with us, signed in with Google or Microsoft. Each asks its own /session, and
 * neither ever renders the other's sign-in.
 */
export function App() {
  return window.location.pathname.startsWith('/admin') ? <Admin /> : <Portal />;
}

/**
 * The admin shell.
 *
 * Everything in it lives under /admin, which is why the routes carry that
 * prefix rather than each view knowing about it.
 */
function Admin() {
  const session = useSession();

  if (!session.ready) {
    return (
      <Box height="100vh" alignItems="center" justifyContent="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (!session.signedIn) return <SignIn providers={session.providers} />;

  return (
    <BrowserRouter>
      <Nav session={session} />
      <Box as="main" className="content-column" padding="4xl 0">
        <Routes>
          <Route path="/admin/controls" element={<ControlsList canEdit={session.canEdit} />} />
          <Route
            path="/admin/controls/:haiID"
            element={<ControlDetail canEdit={session.canEdit} canComment={session.canComment} />}
          />
          <Route path="/admin/pending" element={<Pending />} />
          <Route path="/admin/evidence" element={<EvidenceAdmin canEdit={session.canEdit} />} />
          <Route path="/admin/evidence/runs/:runID" element={<EvidenceRun />} />
          <Route path="/admin/access-reviews" element={<AccessReviews canEdit={session.canEdit} />} />
          <Route
            path="/admin/access-reviews/:reviewID"
            element={<AccessReview canEdit={session.canEdit} />}
          />
          {/*
            Absent rather than guarded: a route that renders a "not for you"
            page is still a page about something they cannot see, and the API
            refuses these anyway. Typing the path lands on the catalogue.
          */}
          {session.seesOperations && (
            <Route path="/admin/gate" element={<GateAdmin canEdit={session.canEdit} />} />
          )}
          {session.seesOperations && (
            <Route path="/admin/users" element={<UsersAdmin session={session} />} />
          )}
          {session.seesOperations && (
            <Route path="/admin/portal" element={<PortalAdmin canEdit={session.canEdit} />} />
          )}
          {/* /admin alone is a route people type. Land them on the catalogue. */}
          <Route path="*" element={<Navigate to="/admin/controls" replace />} />
        </Routes>
      </Box>
    </BrowserRouter>
  );
}

function Nav({ session }) {
  return (
    <Box as="nav" background="primary" borderWidth="0 0 sm 0" borderColor="subtle">
      <Box
        className="content-column"
        direction="row"
        alignItems="center"
        gap="3xl"
        padding="lg 0"
      >
        <NavLink to="/admin/controls" className="app-nav__brand">
          <img src={mark} alt="" aria-hidden="true" width="24" height="24" />
          Hyphen Trust
        </NavLink>

        <Box direction="row" gap="2xl">
          <NavLink to="/admin/controls" className="app-nav__link">Controls</NavLink>
          <NavLink to="/admin/pending" className="app-nav__link">Pending</NavLink>
          <NavLink to="/admin/evidence" className="app-nav__link">Evidence</NavLink>
          <NavLink to="/admin/access-reviews" className="app-nav__link">Access reviews</NavLink>
          {session.seesOperations && (
            <>
              <NavLink to="/admin/gate" className="app-nav__link">Gate</NavLink>
              <NavLink to="/admin/users" className="app-nav__link">Access</NavLink>
              <NavLink to="/admin/portal" className="app-nav__link">Portal</NavLink>
            </>
          )}
        </Box>

        <Box direction="row" alignItems="center" gap="xl" style={{ marginLeft: 'auto' }}>
          <Box as="span" fontSize="sm" color="secondary">{session.email}</Box>
          <Button variant="secondary" size="sm" onClick={session.signOut}>Sign out</Button>
        </Box>
      </Box>
    </Box>
  );
}
